import React, { useState, useEffect, useMemo } from 'react';
import { Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAgents } from '../../../../store/Agent';
import { getAllPhones } from '../../../../store/Phone';
import { getIntegrationTools } from '../../../../store/Integration';

// Import the new field component registry and field options provider
import { fieldComponentRegistry, fieldOptionsProvider } from './FieldComponents';

/**
 * FieldRenderer - Renders form fields dynamically based on node definition
 * Supports conditional field display based on displayRules
 */
const FieldRenderer = ({ 
  configFields = [], 
  displayRules = {}, 
  config = {}, 
  onChange,
  contactProperties = [],
  workflowId = null,
  nodes = [],
  nodeId = null,
  nodeTemplate = null,
  onAddAgent = null,
  onAddPhone = null 
}) => {
  const [visibleFields, setVisibleFields] = useState(new Set());
  
  // Redux hooks
  const dispatch = useDispatch();
  const { allAgents, allAgentsLoading, agentsFetched } = useSelector(state => state.Agent);
  const { allPhones, loading: phonesLoading, phonesFetched } = useSelector(state => state.Phone);
  const tools = useSelector(state => state.Integration?.tools?.data);
  const defaultMappings = useSelector(state => state.Integration?.tools?.defaultMappings) || {};

  // Dynamic tool name mapping based on tools from database
  const toolNameMap = useMemo(() => {
    // If no tools loaded, return fallback mappings from Redux
    if (!tools || tools.length === 0) return defaultMappings;

    const dynamicMap = {};

    tools.forEach(tool => {
      const toolNameLower = tool.name?.toLowerCase() || '';
      const toolKey = toolNameLower.replace(/\s+/g, '');

      // Map common channel names to tool names based on tool category or name
      if (toolNameLower.includes('gmail') || tool.category === 'email') {
        dynamicMap.email = tool.name;
      }
      if (toolNameLower.includes('twilio') || toolKey.includes('sms')) {
        dynamicMap.sms = tool.name;
      }
      if (toolNameLower.includes('whatsapp')) {
        dynamicMap.whatsapp = tool.name;
      }
    });

    return dynamicMap;
  }, [tools, defaultMappings]);

  // Load agents, phones, and tools on mount if needed
  useEffect(() => {
    const needsAgents = configFields.some(field => field.type === 'agent-select');
    const needsPhones = configFields.some(field => field.type === 'phone-select');
    const needsIntegrationSelector = configFields.some(field => field.type === 'integration-selector') ||
      ['email', 'sms', 'whatsapp'].includes(config.channel) ||
      config.triggerType === 'email';

    // Only fetch if we haven't fetched yet (not based on empty results)
    if (needsAgents && !allAgentsLoading && !agentsFetched) {
      dispatch(getAllAgents());
    }
    if (needsPhones && !phonesLoading && !phonesFetched) {
      dispatch(getAllPhones());
    }
    if (needsIntegrationSelector && (!tools || tools.length === 0)) {
      dispatch(getIntegrationTools());
    }
  }, [configFields, config.channel, config.triggerType, dispatch, phonesLoading, phonesFetched, allAgentsLoading, agentsFetched, tools]);

  // Update visible fields when config changes
  useEffect(() => {
    // Evaluate whether a field should be visible based on conditions
    const evaluateFieldVisibility = (field) => {
      // Always show fields defined in showFields
      if (displayRules.showFields?.includes(field.name)) {
        return true;
      }

      // Check conditional fields
      const conditionalRule = displayRules.conditionalFields?.[field.name];
      if (conditionalRule && conditionalRule.showWhen) {
        const conditions = conditionalRule.showWhen;
                
        // Check all conditions - ALL must be true
        let allConditionsMet = true;
        for (const [conditionField, conditionValue] of Object.entries(conditions)) {
          const currentValue = config[conditionField];
          let conditionMet = false;

          
          // Handle array of possible values
          if (Array.isArray(conditionValue)) {
            conditionMet = conditionValue.includes(currentValue);
          } else {
            conditionMet = currentValue === conditionValue;
          }
          
          if (!conditionMet) {
            allConditionsMet = false;
            break;
          } else {
            if (field.name === 'webhookUrl' || field.name === 'authToken') {
              console.log(`    Result: PASSED`);
            }
          }
        }
        
        if (!allConditionsMet) {
          return false;
        }
        
        if (field.name === 'webhookUrl') {
          console.log('  - All conditions passed! Field should be visible.');
        }
        return true;
      }

      // Legacy support: check field.condition
      if (field.condition) {
        for (const [conditionField, conditionValue] of Object.entries(field.condition)) {
          const currentValue = config[conditionField];
          if (Array.isArray(conditionValue)) {
            if (!conditionValue.includes(currentValue)) {
              return false;
            }
          } else if (currentValue !== conditionValue) {
            return false;
          }
        }
        return true;
      }

      // Default to showing fields without conditions
      return true;
    };

    const newVisibleFields = new Set();
    configFields.forEach(field => {
      const isVisible = evaluateFieldVisibility(field);
      if (isVisible) {
        newVisibleFields.add(field.name);
      }

    });

    // IMPORTANT: Preserve dynamically injected fields (like integrationId) that aren't in configFields
    // Check if we need to keep integrationId visible
    const channel = config.channel;
    const needsIntegrationSelector = ['email', 'sms', 'whatsapp'].includes(channel) || config.triggerType === 'email';
    const hasIntegrationFieldInConfig = configFields.some(f => f.name === 'integrationId' || f.type === 'integration-selector');

    if (needsIntegrationSelector && !hasIntegrationFieldInConfig) {
      newVisibleFields.add('integrationId');
      console.log('[FieldRenderer] Preserving integrationId in visibleFields');
    }

    setVisibleFields(newVisibleFields);
  }, [config, configFields, displayRules]);

  // Add dynamically injected integration-selector field to visibleFields
  useEffect(() => {
    const channel = config.channel;
    const needsIntegrationSelector = ['email', 'sms', 'whatsapp'].includes(channel) || config.triggerType === 'email';

    if (needsIntegrationSelector) {
      // Check if integrationId field is NOT already in configFields
      const hasIntegrationField = configFields.some(f => f.name === 'integrationId' || f.type === 'integration-selector');

      if (!hasIntegrationField) {
        console.log('[FieldRenderer useEffect] Adding integrationId to visibleFields');
        setVisibleFields(prev => {
          if (!prev.has('integrationId')) {
            const newSet = new Set(prev);
            newSet.add('integrationId');
            return newSet;
          }
          return prev;
        });
      }
    } else {
      // Remove integrationId from visibleFields if channel changes away from email/sms/whatsapp
      setVisibleFields(prev => {
        if (prev.has('integrationId')) {
          const newSet = new Set(prev);
          newSet.delete('integrationId');
          return newSet;
        }
        return prev;
      });
    }
  }, [config.channel, config.triggerType, configFields]);

  // Handle field value changes
  const handleFieldChange = (fieldName, value) => {
    console.log(`[FieldRenderer] handleFieldChange called: ${fieldName} = ${value}`);
    const newConfig = { ...config, [fieldName]: value };
    console.log('[FieldRenderer] newConfig:', newConfig);

    // Special handling for trigger nodes - sync triggerType with type
    if (fieldName === 'triggerType' && nodeTemplate?.nodeDefinition?.nodeType === 'trigger') {
      newConfig.type = value; // Keep both fields in sync
    }
    
    // Apply field mappings from node definition
    const fieldMappings = nodeTemplate?.nodeDefinition?.behaviors?.fieldMappings || {};
      
      // Handle field change behaviors (like resetting dependent fields)
      if (fieldMappings.onFieldChange?.[fieldName]) {
        const changeConfig = fieldMappings.onFieldChange[fieldName];
        if (changeConfig.resetFields) {
          changeConfig.resetFields.forEach(field => {
            delete newConfig[field];
          });
        }
      }
      
    // Apply field mappings for CRM nodes
    if (fieldMappings[config.operation]) {
      const operationMappings = fieldMappings[config.operation];
      
      Object.entries(operationMappings).forEach(([uiField, mapping]) => {
        if (fieldName === uiField && value) {
          // Check if conditions are met
          let conditionsMet = true;
          if (mapping.condition) {
            Object.entries(mapping.condition).forEach(([condField, condValue]) => {
              if (config[condField] !== condValue) {
                conditionsMet = false;
              }
            });
          }
          
          if (conditionsMet) {
            // Apply the mapping
            const targetPath = mapping.targetField.split('.');
            let target = newConfig;
            
            // Create nested structure if needed
            for (let i = 0; i < targetPath.length - 1; i++) {
              if (!target[targetPath[i]]) {
                target[targetPath[i]] = {};
              }
              target = target[targetPath[i]];
            }
            
            // Set the value
            target[targetPath[targetPath.length - 1]] = value;
          }
        }
      });
    }

    console.log('[FieldRenderer] Calling parent onChange with newConfig:', newConfig);
    onChange(newConfig);
  };

  // Render a single field based on its type
  const renderField = (field) => {
    if (!visibleFields.has(field.name)) {
      console.log(`[FieldRenderer] Field ${field.name} NOT in visibleFields, skipping. visibleFields:`, Array.from(visibleFields));
      return null;
    }

    // Check if this field should preserve its value (for CRM node activity fields)
    const shouldPreserveValue = nodeTemplate?.nodeDefinition?.behaviors?.preserveConfigFields?.includes(field.name);
    const hasConfigValue = config[field.name] !== undefined && config[field.name] !== null && config[field.name] !== '';

    // Determine the value to use:
    // 1. If config has a value (even empty string), use it
    // 2. If config doesn't have the field at all, use default value
    let value;
    if (config.hasOwnProperty(field.name)) {
      // Config has this field (even if null/undefined/empty), use its value
      value = config[field.name];
    } else {
      // Config doesn't have this field, use default
      value = field.type === 'schedule-config' ? undefined : (field.defaultValue || '');
    }



    // Get the appropriate renderer from the field component registry
    const FieldRenderer = fieldComponentRegistry.get(field.type);

    console.log(`[FieldRenderer] Rendering field: ${field.name}, type: ${field.type}, has renderer:`, !!FieldRenderer);
    console.log(`[FieldRenderer] Available renderer types:`, Array.from(fieldComponentRegistry.keys()));

    if (!FieldRenderer) {
      console.error(`[FieldRenderer] No renderer found for field type: ${field.type}`);
      return (
        <Alert severity="warning" key={field.name}>
          Unsupported field type: {field.type} for field {field.name}
        </Alert>
      );
    }

    // Render the field using the registered component
    return (
      <FieldRenderer
        key={field.name}
        field={field}
        value={value}
        onChange={handleFieldChange}
        config={config}
        nodeTemplate={nodeTemplate || { nodeDefinition: { displayRules, fieldConfigurations: {} } }}
        dynamicFieldOptions={fieldOptionsProvider}
        fetchFieldOptions={(endpoint, params, config) => 
          fieldOptionsProvider.fetchFieldOptions(endpoint, params, config)
        }
        allAgents={allAgents}
        allAgentsLoading={allAgentsLoading}
        allPhones={allPhones}
        phonesLoading={phonesLoading}
        onAddAgent={onAddAgent}
        onAddPhone={onAddPhone}
        workflowId={workflowId}
        nodeId={nodeId}
        nodes={nodes}
        contactProperties={contactProperties}
        disabled={false}
      />
    );

    // All field types are now handled by fieldComponentRegistry
  };

  // Get info alerts from node definition
  const getInfoAlert = () => {
    if (!nodeTemplate?.nodeDefinition?.uiMessages?.infoAlerts) return null;
    
    const alerts = nodeTemplate.nodeDefinition.uiMessages.infoAlerts;
    
    // Check for operation-specific alerts
    if (config.operation && alerts[config.operation]) {
      return alerts[config.operation];
    }
    
    // Check for general alerts based on other config
    const alertKeys = Object.keys(config).filter(key => alerts[config[key]]);
    if (alertKeys.length > 0) {
      return alerts[config[alertKeys[0]]];
    }
    
    return null;
  };

  const infoAlert = getInfoAlert();

  // Dynamically inject integration-selector field for communication nodes and email triggers
  const getFieldsWithIntegrationSelector = () => {
    const fields = [...configFields];

    // Check if this is a communication node with email/SMS/WhatsApp channel
    const channel = config.channel;
    const isEmailTrigger = config.triggerType === 'email';
    console.log('[FieldRenderer] Checking for integration selector');
    console.log('[FieldRenderer] Channel:', channel);
    console.log('[FieldRenderer] Config:', config);

    const needsIntegrationSelector = ['email', 'sms', 'whatsapp'].includes(channel) || isEmailTrigger;
    console.log('[FieldRenderer] Needs integration selector?', needsIntegrationSelector);

    if (needsIntegrationSelector) {
      // Use dynamic toolNameMap from tools data
      const toolName = isEmailTrigger ? (toolNameMap['email'] || 'Gmail') : (toolNameMap[channel] || channel);

      // Check if integration-selector field already exists (also skip if connection-status placeholder is present)
      const hasIntegrationField = fields.some(f => f.name === 'integrationId' || f.type === 'integration-selector');

      if (!hasIntegrationField) {
        console.log('[FieldRenderer] Integration field does not exist, injecting...');

        let insertIndex;
        if (isEmailTrigger) {
          // For email trigger: replace the connection-status field (gmailConnection) if present, else insert after triggerType
          const connectionStatusIndex = fields.findIndex(f => f.type === 'connection-status');
          if (connectionStatusIndex >= 0) {
            // Replace connection-status with integration-selector
            fields.splice(connectionStatusIndex, 1);
            insertIndex = connectionStatusIndex;
          } else {
            const triggerTypeIndex = fields.findIndex(f => f.name === 'triggerType');
            insertIndex = triggerTypeIndex >= 0 ? triggerTypeIndex + 1 : 0;
          }
        } else {
          // Find index to insert after channel field or at the beginning
          const channelFieldIndex = fields.findIndex(f => f.name === 'channel');
          insertIndex = channelFieldIndex >= 0 ? channelFieldIndex + 1 : 0;
        }

        // Create integration-selector field
        const integrationField = isEmailTrigger
          ? {
              name: 'integrationId',
              label: 'Gmail Account',
              type: 'integration-selector',
              toolName: toolName,
              placeholder: `Select ${toolName} account to receive emails`,
              description: `Choose which ${toolName} account to watch for incoming emails`,
              required: true
            }
          : {
              name: 'integrationId',
              label: 'Send From',
              type: 'integration-selector',
              toolName: toolName,
              placeholder: `Select ${toolName} account to send from`,
              description: `Choose which ${toolName} account to use for sending`,
              required: false
            };

        console.log('[FieldRenderer] Injecting integration field at index:', insertIndex);
        console.log('[FieldRenderer] Integration field:', integrationField);

        // Insert the field
        fields.splice(insertIndex, 0, integrationField);
        console.log('[FieldRenderer] Fields after injection:', fields);
      } else {
        console.log('[FieldRenderer] Integration field already exists');
      }
    }

    return fields;
  };

  const fieldsToRender = getFieldsWithIntegrationSelector();

  console.log('[FieldRenderer] Total fields to render:', fieldsToRender.length);
  console.log('[FieldRenderer] Fields being rendered:', fieldsToRender.map(f => `${f.name} (${f.type})`));

  return (
    <>
      {fieldsToRender.map(field => renderField(field))}
      
      {/* Dynamic info alerts from node definition */}
      {infoAlert && (
        <Alert severity="info" sx={{ fontSize: '0.75rem', mt: 2 }}>
          {infoAlert}
        </Alert>
      )}
    </>
  );
};

export default FieldRenderer;